if (!customElements.get('bf-public-tracker')) {
  customElements.define(
    'bf-public-tracker',
    class BFPublicTracker extends HTMLElement {
      connectedCallback() {
        this.directForm = this.querySelector('[data-bf-direct-form]');
        this.portalForm = this.querySelector('[data-bf-portal-form]');
        this.portalPanel = this.portalForm?.closest('details');
        this.result = this.querySelector('[data-bf-tracker-result]');
        this.projects = this.querySelector('[data-bf-projects]');
        this.accountMessage = this.querySelector('[data-bf-account-message]');
        this.directForm?.addEventListener('submit', (event) => this.directLookup(event));
        this.portalForm?.addEventListener('submit', (event) => this.sendPortalLink(event));

        this.lookupFromUrl();

        if (this.dataset.customerSignedIn === 'true') {
          this.hidePortalLinkFallback();
        }

        if (this.dataset.customerSignedIn === 'true' && this.accountMessage && this.projects) {
          this.loadProjects();
        }
      }

      endpoint() {
        return new URL(this.dataset.endpoint || '/apps/frame-production', window.location.origin);
      }

      async loadProjects() {
        try {
          const response = await fetch(this.endpoint(), {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          const data = await response.json();

          if (!response.ok) {
            if (this.accountMessage) this.accountMessage.textContent = data.error || 'Your projects could not be loaded.';
            return;
          }

          this.renderProjects(data.projects || []);
        } catch {
          if (this.accountMessage) this.accountMessage.textContent = 'Your projects are temporarily unavailable. Please try again.';
        }
      }

      renderProjects(projects) {
        if (!this.projects || !this.accountMessage) return;

        this.projects.replaceChildren();
        this.projects.hidden = false;

        if (!projects.length) {
          this.accountMessage.textContent =
            'No projects are linked to this email yet. You can request a private tracker link above.';
          return;
        }

        this.accountMessage.textContent = `${projects.length} frame project${projects.length === 1 ? '' : 's'}`;
        projects.forEach((project) => this.projects.append(this.projectCard(project, true)));
      }

      renderPortalProjects(projects) {
        this.hidePortalLinkFallback();
        this.result.hidden = false;
        this.result.classList.remove('bf-public-tracker__error');
        this.result.replaceChildren();

        const summary = this.element(
          'p',
          `${projects.length} linked frame project${projects.length === 1 ? '' : 's'}`
        );
        this.result.append(summary);
        projects.forEach((project) => this.result.append(this.projectCard(project, false)));
      }

      projectCard(project, showPreference) {
        const card = this.element('article', '', 'bf-public-tracker__project');
        const statusLabel = this.projectStatusLabel(project);
        const topLine = this.element('div', '', 'bf-public-tracker__tracker-topline');
        topLine.append(
          this.element('span', 'Project tracker'),
          this.element('strong', statusLabel)
        );

        const overview = this.element('div', '', 'bf-public-tracker__overview');
        overview.append(this.element('span', '', 'bf-public-tracker__thumb'));

        const overviewText = this.element('div', '', 'bf-public-tracker__overview-text');
        overviewText.append(this.element('h2', project.name || 'Custom Frame Project'));

        const specs = this.projectSpecs(project);
        if (specs.length) {
          overviewText.append(this.element('p', specs.join(' | '), 'bf-public-tracker__specs'));
        }

        const currentStep = this.currentStepText(project);
        if (currentStep) {
          overviewText.append(this.element('small', `Current step: ${currentStep}`));
        }

        overview.append(overviewText);
        card.append(topLine, overview);

        if (project.progress) {
          card.append(this.progressBlock(project.progress));
          card.append(this.milestoneSteps(project));
        }

        const statusGrid = this.statusGrid(project);
        if (statusGrid) {
          card.append(statusGrid);
        }
        if (project.matProgress) {
          card.append(this.matProgressBlock(project.matProgress));
        }

        if (project.updatedAt) {
          card.append(
            this.element('p', `Last updated: ${new Date(project.updatedAt).toLocaleDateString()}`, 'bf-public-tracker__updated')
          );
        }

        if (showPreference) {
          const preference = this.element('label', '', 'bf-public-tracker__preference');
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = Boolean(project.emailUpdatesEnabled);
          input.disabled = !project.canEmail;
          input.addEventListener('change', () => this.updateEmailPreference(project, input));
          preference.append(
            input,
            document.createTextNode(
              project.canEmail
                ? ' Email me when this frame moves to a new stage'
                : " Ask Blazin' Frames to link an email address for updates"
            )
          );
          card.append(preference);
        }

        return card;
      }

      projectStatusLabel(project) {
        if (project.progress?.complete) return 'Complete';
        return project.currentProductionStage?.label || project.progress?.currentStage || 'In Progress';
      }

      currentStepText(project) {
        return (
          project.currentProductionStage?.detail ||
          project.stageDetail ||
          project.progress?.currentStage ||
          project.stage ||
          ''
        );
      }

      projectSpecs(project) {
        return [
          project.orderNumber ? `Order ${project.orderNumber}` : null,
          project.frameSize,
          project.woodSpecies,
        ].filter(Boolean);
      }

      milestoneSteps(project) {
        const percent = Math.max(0, Math.min(100, Number(project.progress?.percent) || 0));
        const activeIndex = this.milestoneIndex(project, percent);
        const steps = [
          'Order received',
          'Wood selected',
          'Build in progress',
          'Assembly',
          'Crating',
          'Shipped',
        ];

        const list = this.element('ol', '', 'bf-public-tracker__steps');
        list.setAttribute('aria-label', 'Frame production milestones');

        steps.forEach((label, index) => {
          const item = this.element('li', label);
          if (index < activeIndex) {
            item.className = 'is-complete';
          } else if (index === activeIndex) {
            item.className = 'is-active';
          }
          list.append(item);
        });

        return list;
      }

      milestoneIndex(project, percent) {
        const stageText = [
          project.currentProductionStage?.label,
          project.currentProductionStage?.detail,
          project.progress?.currentStage,
          project.stage,
          project.stageDetail,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (project.progress?.complete || stageText.includes('shipped')) return 5;
        if (stageText.includes('crat') || stageText.includes('pack')) return 4;
        if (stageText.includes('assembly') || stageText.includes('review')) return 3;
        if (
          stageText.includes('build') ||
          stageText.includes('construct') ||
          stageText.includes('burn') ||
          stageText.includes('epoxy') ||
          stageText.includes('finish')
        ) {
          return 2;
        }
        if (stageText.includes('wood') || stageText.includes('material')) return 1;

        if (percent >= 100) return 5;
        if (percent >= 86) return 4;
        if (percent >= 68) return 3;
        if (percent >= 30) return 2;
        if (percent >= 12) return 1;
        return 0;
      }

      statusGrid(project) {
        const items = [
          {
            label: 'Current production stage',
            value: project.currentProductionStage?.label || project.progress?.currentStage,
            detail: project.currentProductionStage?.detail || project.stageDetail,
            complete: project.currentProductionStage?.complete,
          },
          {
            label: 'Payment status',
            value: project.paymentStatus?.label,
            detail: project.paymentStatus?.detail,
            actionNeeded: project.paymentStatus?.actionNeeded,
            complete: project.paymentStatus?.complete,
          },
          {
            label: 'Approval status',
            value: project.approvalStatus?.label,
            detail: project.approvalStatus?.detail,
            actionNeeded: project.approvalStatus?.actionNeeded,
            complete: project.approvalStatus?.complete,
          },
          {
            label: 'Estimated completion',
            value: project.estimatedCompletionWindow?.window,
            detail: project.estimatedCompletionWindow?.detail,
            complete: project.progress?.complete,
          },
          {
            label: 'Shipping',
            value: project.trackingStatus?.label || (project.trackingNumber ? 'Tracking available' : null),
            detail: project.trackingStatus?.trackingNumber
              ? `Tracking number: ${project.trackingStatus.trackingNumber}`
              : project.trackingStatus?.detail,
            complete: project.trackingStatus?.complete,
          },
        ].filter((item) => item.value);

        if (!items.length) return null;

        const grid = this.element('dl', '', 'bf-public-tracker__status-grid');
        items.forEach((item) => {
          const modifier = item.actionNeeded
            ? ' bf-public-tracker__status-card--action'
            : item.complete
              ? ' bf-public-tracker__status-card--complete'
              : '';
          const card = this.element('div', '', `bf-public-tracker__status-card${modifier}`);
          card.append(
            this.element('dt', item.label),
            this.element('dd', item.value)
          );
          if (item.detail) {
            card.append(this.element('small', item.detail));
          }
          grid.append(card);
        });

        return grid;
      }

      progressBlock(progress) {
        const percent = Math.max(0, Math.min(100, Number(progress.percent) || 0));
        const section = this.element('section', '', 'bf-public-tracker__progress');
        const header = this.element('div', '', 'bf-public-tracker__progress-header');
        header.append(
          this.element('strong', `${percent}% complete`),
          this.element('span', `Step ${progress.currentStep} of ${progress.totalSteps}`)
        );

        const track = this.element('div', '', 'bf-public-tracker__progress-track');
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', '100');
        track.setAttribute('aria-valuenow', String(percent));
        track.setAttribute('aria-label', `Frame production is ${percent}% complete`);
        const fill = this.element('span', '', 'bf-public-tracker__progress-fill');
        fill.style.width = `${percent}%`;
        track.append(fill);

        section.append(header, track);
        if (progress.nextStage) {
          section.append(this.element('small', `Next up: ${progress.nextStage}`));
        } else if (progress.complete) {
          section.append(this.element('small', 'Complete: your frame has shipped.'));
        }

        return section;
      }

      matProgressBlock(matProgress) {
        const total = Number(matProgress.total) || 3;
        const completed = Math.max(0, Math.min(total, Number(matProgress.completed) || 0));
        const percent = Math.round((completed / total) * 100);
        const section = this.element('section', '', 'bf-public-tracker__mat-progress');
        const header = this.element('div', '', 'bf-public-tracker__progress-header');
        header.append(
          this.element('strong', matProgress.label || 'Artwork & mats'),
          this.element('span', `${completed} of ${total} complete`)
        );

        const track = this.element('div', '', 'bf-public-tracker__progress-track bf-public-tracker__progress-track--mat');
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', '100');
        track.setAttribute('aria-valuenow', String(percent));
        track.setAttribute('aria-label', `Artwork and mat prep is ${percent}% complete`);
        const fill = this.element('span', '', 'bf-public-tracker__progress-fill');
        fill.style.width = `${percent}%`;
        track.append(fill);

        const steps = this.element('div', '', 'bf-public-tracker__mat-steps');
        (matProgress.steps || []).forEach((step) => {
          const pill = this.element(
            'span',
            `${step.label}: ${step.complete ? 'done' : 'pending'}`,
            `bf-public-tracker__mat-step${step.complete ? ' bf-public-tracker__mat-step--complete' : ''}`
          );
          steps.append(pill);
        });

        section.append(header, track, steps);
        return section;
      }

      hidePortalLinkFallback() {
        if (this.portalPanel) this.portalPanel.hidden = true;
      }

      async updateEmailPreference(project, input) {
        input.disabled = true;
        const body = new URLSearchParams({
          projectId: project.id,
          enabled: input.checked ? 'true' : 'false',
        });

        try {
          const response = await fetch(this.endpoint(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            credentials: 'same-origin',
            body,
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Unable to save email preference.');
          project.emailUpdatesEnabled = input.checked;
          if (this.accountMessage) {
            this.accountMessage.textContent = input.checked
              ? 'Email updates enabled.'
              : 'Email updates turned off.';
          }
        } catch (error) {
          input.checked = !input.checked;
          if (this.accountMessage) {
            this.accountMessage.textContent =
              error instanceof Error ? error.message : 'Unable to save email preference.';
          }
        } finally {
          input.disabled = !project.canEmail;
        }
      }

      lookupFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const portal = params.get('portal');
        if (portal) {
          this.loadPortal(portal);
        }
      }

      async loadPortal(portalToken) {
        this.renderMessage('Opening your private tracker...');
        try {
          const endpoint = this.endpoint();
          endpoint.searchParams.set('portal', portalToken.toString().trim());
          const response = await fetch(endpoint, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          const data = await response.json();

          if (!response.ok) {
            this.renderMessage(data.error || 'This private tracker link could not be opened.', true);
            return;
          }

          if (Array.isArray(data.projects)) {
            this.renderPortalProjects(data.projects);
            return;
          }

          this.result.hidden = false;
          this.result.classList.remove('bf-public-tracker__error');
          this.hidePortalLinkFallback();
          this.result.replaceChildren(this.projectCard(data, false));
        } catch {
          this.renderMessage('The tracker is temporarily unavailable. Please try again.', true);
        }
      }

      async sendPortalLink(event) {
        event.preventDefault();
        const formData = new FormData(this.portalForm);
        const email = formData.get('email')?.toString().trim();
        if (!email) return;

        this.renderMessage('Sending your private tracker link...');
        try {
          const response = await fetch(this.endpoint(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            credentials: 'same-origin',
            body: new URLSearchParams(formData),
          });
          const data = await response.json();

          if (!response.ok) {
            this.renderMessage(data.error || 'Unable to send your private tracker link.', true);
            return;
          }

          this.renderMessage(data.message || "If that email is linked to a Blazin' Frames project, a private tracker link has been sent.");
          this.portalForm.reset();
        } catch {
          this.renderMessage('The tracker is temporarily unavailable. Please try again.', true);
        }
      }

      async directLookup(event) {
        event.preventDefault();
        const formData = new FormData(this.directForm);
        const email = formData.get('email')?.toString().trim();
        if (!email) return;

        this.renderMessage('Looking up your frame projects...');
        try {
          const response = await fetch(this.endpoint(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            credentials: 'same-origin',
            body: new URLSearchParams(formData),
          });
          const data = await response.json();

          if (!response.ok) {
            this.renderMessage(data.error || 'No active frame projects were found for that email address.', true);
            return;
          }

          if (Array.isArray(data.projects)) {
            this.renderPortalProjects(data.projects);
            return;
          }

          this.result.hidden = false;
          this.result.classList.remove('bf-public-tracker__error');
          this.hidePortalLinkFallback();
          this.result.replaceChildren(this.projectCard(data, false));
        } catch {
          this.renderMessage('The tracker is temporarily unavailable. Please try again.', true);
        }
      }

      element(tag, text, className) {
        const element = document.createElement(tag);
        if (text) element.textContent = text;
        if (className) element.className = className;
        return element;
      }

      renderMessage(message, isError = false) {
        this.result.hidden = false;
        this.result.replaceChildren(this.element('p', message));
        this.result.classList.toggle('bf-public-tracker__error', isError);
      }
    }
  );
}
